import React from 'react';
import { useSelector } from 'react-redux';
import { FileNode, FileNodeTypeEnum } from '../../api-client/autogenerated';
import { getProjectState } from '../../features/project/selectors';
import {
  getDirectDesignFileDownloadLink,
  getDownloadLinkForTrashItem,
  sendPublicFolderAccessToEveryone,
} from '../../models/api/filesystem';
import { shareDocumentPublicLinks } from '../../models/api/documents';
import EmailUsersDialog from './EmailUsersDialog';

type Props = {
  open: boolean;
  handleClose: () => void;
  /**
   * What is being shared. The first is the one a link points at -- a link is to a single file or
   * folder -- while a notification names all of them, which is how sharing a multi-selection works.
   */
  files: FileNode[];
  isRecycleBin?: boolean;
};

/**
 * Sharing a file or folder from CENTERLINE Drive, with everything the two kinds of recipient need
 * in one place.
 *
 * A CENTERLINE user is notified against the record -- the notification appears in the product and
 * shows up in the file's activity log. Anyone else is not in the project and has nothing to log
 * into, so they are sent a link instead: a presigned download for a file, a public share for a
 * folder. The dialog offers both, and sends whichever it was actually given recipients for, so the
 * user picks people rather than picking which mechanism to use. Copy Link and Download QR Code sit
 * underneath for a link that is not going out by email at all.
 */
export default function DriveShareDialog(props: Props) {
  const { open, handleClose, files, isRecycleBin } = props;

  const project = useSelector(getProjectState);

  const file = files[0];
  const isFile = file?.type === FileNodeTypeEnum.File;
  // One link points at one thing, so Copy Link and the QR code are only offered for a single
  // selection. Sending by email carries the whole selection either way.
  const isSingle = files.length === 1;

  const getLink = async (): Promise<string> => {
    if (!file || !project) return '';
    if (isRecycleBin) return getDownloadLinkForTrashItem(file.fullKey);
    if (isFile) {
      const result = await getDirectDesignFileDownloadLink(project.id, file.fullKey, file.ownerId);
      return result.data.downloadLink;
    }
    try {
      const res = await sendPublicFolderAccessToEveryone(project.id, file.fullKey, file.ownerId);
      return res.data;
    } catch {
      return '';
    }
  };

  /**
   * One email listing everything that was selected, rather than one email per file. The share
   * endpoint already builds exactly that -- it is what the Send Anything card sends -- so the
   * recipients here get the same message for a Drive selection as they would from the dashboard.
   * Only the typed addresses go through it: the CENTERLINE users EmailUsersDialog picked are
   * notified against the files themselves, which is what puts the share in their activity logs.
   */
  const sendLinkTo = async (emails: string[], note?: string) => {
    if (!project || files.length === 0) return;
    await shareDocumentPublicLinks({
      projectId: project.id,
      documentIds: [],
      fileNodes: files.map(({ fullKey, ownerId }) => ({ fullKey, ownerId })),
      userIds: [],
      emails,
      subject: undefined,
      body: note,
    });
  };

  if (!file) return null;

  return (
    <EmailUsersDialog
      dialogOpen={open}
      closeDialog={handleClose}
      title="Send"
      files={files}
      isDesignNotify
      onSubmit={sendLinkTo}
      getLink={isSingle ? getLink : undefined}
      // A file link is a presigned download URL and costs nothing to fetch, but a folder link is
      // backed by a share row -- so it waits for the click that asks for it.
      generateLinkOnOpen={isSingle && isFile}
      initialSubject={
        !isSingle
          ? `${files.length} items shared from ${project?.name}`
          : isFile
          ? `New File Share in ${project?.name}`
          : `New Folder Share in ${project?.name}`
      }
    />
  );
}
